import { HttpService } from './http.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NGXLogger } from "ngx-logger";
import { dashboard, gridItem, interaction, property, rule } from 'src/app/models'
import { agent, dashboardConfig, report, reportCategory, reportStorageInfo, saveReportModel, updateCategoriesListModel } from 'src/app/dashboards/models';
import { isDashboardEditable } from 'src/app/dashboards/dashboard-helpers/helpers';
import { reportSources } from 'src/app/reports/reports-list/reports-list.component';


@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  public Dashboards: Array<dashboard>;

  constructor(private http: HttpService, private logger: NGXLogger) { }

  public getConfiguration(): Observable<dashboardConfig> {
    return this.http.getFromApi('/dashboards/configuration');
  }

  public getDashboards(): Observable<Array<dashboard>> {
    return this.http.getFromApi('/dashboards');
  }

  public getOwnDashboards(): Observable<Array<dashboard>> {
    return this.http.getFromApi('/OwnDashboards');
  }

  public getDashboardsById(dashboardIds: number[]): Observable<Array<dashboard>> {
    return this.http.postToApi('/Dashboardsbyid', JSON.stringify({ dashboardIds }));
  }

  public createReportInWidget(dashboard: dashboard) {
    return this.http.postToApi(`/ExportReportToDashboard`, JSON.stringify(dashboard));
  }

  public updateDashboards(dashboards: dashboard[], userInfo: any): Observable<Array<dashboard>> {
    let userProps = userInfo?.userProperties;
    //Send  widgets only for editable dashboards and those that changed access visibility
    let payload = dashboards.map(d => d.isAccessChanged || d.isAllowNonOwnersToEditChanged || d.isOwnerUpdated ||
                                      isDashboardEditable(d, userProps?.isRoot, userProps?.isPartner, userProps?.allowToEditOtherUsersDashboards,userProps?.isDashboardsReadOnly) ?
                                      d :
                                      ({ ...d, tabs: [] }));

    return this.http.postToApi(`/dashboards`, JSON.stringify(payload));
  }

  public getAllQueues(): Observable<Array<any>> {
    return this.http.getFromApi('/Dashboards/GetAllQueues');
  }

  public getAllAgents(): Observable<Array<agent>> {
    return this.http.getFromApi('/Dashboards/GetAllAgents');
  }

  public getAgentProperties(): Observable<Array<property>> {
    return this.http.getFromApi('/Dashboards/GetAgentProperties');
  }

  public getQueueProperties(): Observable<Array<property>> {
    return this.http.getFromApi('/Dashboards/GetQueueProperties');
  }

  public getQueueData(queues: number[], props: number[]): Observable<Array<any>> {
    let body = JSON.stringify({ queues, props });
    return this.http.postToApi('/Dashboards/GetQueueData', body);
  }

  public getAgentData(agents: number[], props: number[]): Observable<Array<any>> {
    let body = JSON.stringify({ agents, props });
    return this.http.postToApi('/Dashboards/GetAgentData', body);
  }

  public getTickerData(queues: number[], props: number[]): Observable<Array<any>> {
    let body = JSON.stringify({ queues, props });
    return this.http.postToApi('/Dashboards/GetTickerData', body);
  }

  public updateDashboardOwner(dashboard): Observable<Array<any>> {
    let body = JSON.stringify(dashboard);
    return this.http.postToApi('/UpdatedDashboardOwner', body);
  }

  public getReportsEnabled(): Observable<boolean> {
    return this.http.getFromApi('/reports/enabled');
  }

  public getWebEngageRuleMatchEnabled(): Observable<boolean> {
    return this.http.getFromApi('/WebEngageReports/rulematchenabled');
  }

  public getReportsCanEdit(): Observable<boolean> {
    return this.http.getFromApi('/reports/canedit');
  }

  public getReports(): Observable<Array<report>> {
    return this.http.getFromApi('/reports/getreportslist');
  }
  public getPccReports(): Observable<Array<report>> {
    return this.http.getFromApi('/reports/getreportslist?reportSource=1');
  }
  public getAllReports(): Observable<Array<report>> {
    return this.http.getFromApi('/reports/getreportslist?includeSubReports=true');
  }
  public getReport(id: number, subReportId: number): Observable<report> {
    return this.http.getFromApi(`/reports/getreport?id=${id}&subId=${subReportId}`);
  }

  public getReportByCode(reportCode: string, reportSource: reportSources): Observable<report> {
    return this.http.getFromApi(`/reports/getreportbycode?code=${reportCode}&reportSource=${reportSources}`);
  }

  public getServiceNumbers(): Observable<Array<any>> {
    return this.http.getFromApi('/reports/ServiceNumbers');
  }

  public getReportColumns(id: number, subReportId: number): Observable<Array<any>> {
    return this.http.getFromApi(`/reports/GetReportColumns?id=${id}&subId=${subReportId}`);
  }

  public getReportRowsFilter(type: string, globalService: string): Observable<Array<any>> {
    return this.http.getFromApi(`/reports/rowsfilter?type=${type}&globalService=${encodeURIComponent(globalService)}`);
  }

  public GetReportResult(setup: any): Observable<any> {
    let body = JSON.stringify(setup);
    return this.http.postToApi('/Reports/GetReportResult', body);
  }

  public SaveReport(setup: saveReportModel): Observable<any> {
    let body = JSON.stringify(setup);
    return this.http.postToApi('/Reports/SaveReport', body);
  }

  public getReportCategories(): Observable<Array<reportCategory>> {
    return this.http.getFromApi(`/reports/getCategoryList`);
  }

  public updateReportCategories(setup: updateCategoriesListModel): Observable<any> {
    let body = JSON.stringify(setup);
    return this.http.postToApi('/Reports/UpdateCategoryList', body);
  }

  public exportTableCSV(data) {
    let body = JSON.stringify(data);
    return this.http.postToApiFile('/Reports/ExportCSV', body);
  }

  public exportTableXLSX(data) {
    let body = JSON.stringify(data);
    return this.http.postToApiFile('/Reports/ExportXLSX', body);
  }

  deleteReport(subReportId: number) {
    let body = JSON.stringify(subReportId);
    return this.http.postToApi('/reports/deleteReport', body);
  }

  public getStorageInfo(): Observable<Array<reportStorageInfo>> {
    return this.http.getFromApi(`/reports/getStorageInfo`);
  }

  public getUserInfo(): Observable<any> {
    return this.http.getFromApi(`/User/Info`);
  }

  public getDashboardUsage(dashboardId: number): Observable<number> {
    return this.http.getFromApi(`/dashboards/getDashboardUsage?dashboardId=${dashboardId}`);
  }

  public getDashboardPageAccess(): Observable<boolean> {
    return this.http.getFromApi(`/admin/canactivate?path=dashboard`);
  }

  public getStatisticsPageAccess(): Observable<boolean> {
    return this.http.getFromApi(`/admin/canactivate?path=reports`);
  }

  public getRuleList(from: Date, to: Date): Observable<Array<rule>> {
    return this.http.getFromApi(`/WebEngageReports/GetRulesList?from=${encodeURIComponent(from.toJSON())}&to=${encodeURIComponent(to.toJSON())}`);
  }

  public getInteractionList(from: Date, to: Date): Observable<Array<interaction>> {
    return this.http.getFromApi(`/WebEngageReports/GetInteractionsList?from=${encodeURIComponent(from.toJSON())}&to=${encodeURIComponent(to.toJSON())}`);
  }

  public getWebEngageReportResult(setup: any): Observable<any> {
    let body = JSON.stringify(setup);
    return this.http.postToApi('/WebEngageReports/GetReportResult', body);
  }

  public getWebEngageReportsCanEdit(): Observable<boolean> {
    return this.http.getFromApi('/WebEngageReports/CanEdit');
  }

  public ExportReportToFile(setup: any, reportTitle: string, fileType: string, isTotalRequestedToBeShown: boolean): Observable<any> {
    let body = JSON.stringify({ reportSetup: setup, reportTitle, fileType, isTotalRequestedToBeShown });
    return this.http.postToApiFile(`/Reports/ExportReportInFile`, body);
  }

  public ExportWebEngageReportToFile(setup: any, reportTitle: string, fileType: string, isTotalRequestedToBeShown: boolean): Observable<any> {
    let body = JSON.stringify({ reportSetup: setup, reportTitle, fileType, isTotalRequestedToBeShown });
    return this.http.postToApiFile(`/WebEngage/ExportReportInFile`, body);
  }

  public getSecondDownloadButtonFeatureFlag(): Observable<boolean> {
    return this.http.getFromApi('/Reports/GetSecondDownloadButtonFeatureFlag');
  }
}
